import React from 'react';
import styled from 'styled-components';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import SEO from '../../common/SEO';
import { getCanonicalUrl } from '../../../utils/seo';

const castleImage = 'https://BelvoirCastle.b-cdn.net/Castle/castle.webp';

// Import a relevant image - using castle image as default

const DroneContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const PolicyContent = styled.div`
  background-color: white;
  padding: 3rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const PolicyTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin: 2rem 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionContent = styled.div`
  margin-bottom: 2rem;
`;

const PolicyText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const BulletList = styled.ul`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 1rem 0 1rem 2rem;
`;

const BulletItem = styled.li`
  margin-bottom: 0.5rem;
`;

const HighlightBox = styled.div`
  background-color: rgba(218, 165, 32, 0.1);
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0 8px 8px 0;
`;

const ContactInfo = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  padding: 2rem;
  border-radius: 8px;
  margin-top: 2rem;
  text-align: center;
`;

const ContactTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const DronePolicy = () => {
  return (
    <DroneContainer>
      <SEO 
        title="Belvoir Castle Drone Policy | UAV Guidelines & Permissions"
        description="Read Belvoir Castle's drone policy. Learn about UAV restrictions, permission requirements, and safety guidelines for flying drones on the estate."
        keywords="Belvoir Castle drone policy, UAV restrictions, drone permissions, private property drone rules, estate drone guidelines"
        canonicalUrl={getCanonicalUrl('drone-policy')}
        ogImage={castleImage}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Belvoir Castle Drone Policy",
          "description": "Comprehensive drone policy for Belvoir Castle Estate covering UAV restrictions, permissions, and safety guidelines.",
          "url": getCanonicalUrl('drone-policy'),
          "mainEntity": {
            "@type": "Policy",
            "name": "Belvoir Castle Drone Policy",
            "description": "Policy regarding the use of drones and UAVs on Belvoir Castle Estate property."
          }
        }}
      />
      <ParallaxBanner 
        title="Drone Policy"
        subtitle="UAV Guidelines & Permissions"
        image={castleImage}
      />
      
      <ContentContainer>
        <PolicyContent>
          <PolicyTitle>Belvoir Castle Drone Policy</PolicyTitle>
          
          <IntroText>
            To protect visitor privacy, ensure safety, and preserve the tranquillity and heritage of the Estate, 
            the use of drones (UAVs) is not permitted over Belvoir Castle or its grounds without express, 
            written permission from the Duke and Duchess of Rutland.
          </IntroText>

          <SectionTitle>✋ Flying Without Permission</SectionTitle>
          <SectionContent>
            <PolicyText>
              Belvoir Castle and its Estate lands are private property. Any drone flying from or over this area 
              without approval may be considered:
            </PolicyText>
            <BulletList>
              <BulletItem>
                <strong>Trespass</strong>, if it interferes with the landowner's enjoyment or use of the land
              </BulletItem>
              <BulletItem>
                <strong>A privacy breach</strong>, particularly during weddings, private events or filming
              </BulletItem>
              <BulletItem>
                <strong>A safety hazard</strong>, especially during livestock movement or aerial operations
              </BulletItem>
            </BulletList>
          </SectionContent>

          <SectionTitle>🛑 Sub-250g Drone Myth</SectionTitle>
          <SectionContent>
            <PolicyText>
              We understand that drones under 250g (such as DJI Mini models) are subject to fewer restrictions 
              under CAA law. However, this does not grant permission to fly over private land, nor does it 
              override the responsibility to maintain:
            </PolicyText>
            <BulletList>
              <BulletItem>Visual line of sight at all times</BulletItem>
              <BulletItem>A minimum safe distance from uninvolved people</BulletItem>
              <BulletItem>Respect for privacy, property, and safety</BulletItem>
            </BulletList>
            
            <HighlightBox>
              <PolicyText style={{ margin: 0, fontWeight: '500' }}>
                Given the size and topography of the Belvoir Estate, it is impossible to legally 
                and safely operate a drone over the Castle from public land without breaching one or more 
                of these principles.
              </PolicyText>
            </HighlightBox>
          </SectionContent>

          <ContactInfo>
            <ContactTitle>Need Permission?</ContactTitle>
            <PolicyText style={{ margin: 0 }}>
              For professional filming, photography, or other legitimate purposes requiring drone use, 
              please contact us in advance to discuss your requirements and obtain written permission.
            </PolicyText>
          </ContactInfo>
        </PolicyContent>
      </ContentContainer>
      
      <Footer />
    </DroneContainer>
  );
};

export default DronePolicy; 